import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { adminAPI, authAPI } from '../services/api';
import { evaApi } from '../services/evaApi';

const AdminContext = createContext({});

export const useAdmin = () => useContext(AdminContext);

const emptyStats = {
  available: false,
  totalUsers: null,
  activeUsers: null,
  pendingUsers: null,
  newUsersLast7Days: null,
};

const checking = (name) => ({ name, state: 'checking', detail: 'Проверяем подключение…' });
const offline = (name, error) => ({ name, state: 'offline', detail: error || 'Сервис не ответил.' });
const online = (name, detail) => ({ name, state: 'online', detail });

export const AdminProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [stats, setStats] = useState(emptyStats);
  const [recentUsers, setRecentUsers] = useState([]);
  const [servicesStatus, setServicesStatus] = useState({
    oauth: checking('OAuth'),
    eva: checking('EvaAI'),
  });
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const loadData = useCallback(async () => {
    if (!isAuthenticated) {
      setStats(emptyStats);
      setRecentUsers([]);
      return;
    }

    setIsLoading(true);
    const [tokenResult, usersResult, evaResult] = await Promise.allSettled([
      authAPI.verifyToken(),
      adminAPI.getUsers({ page: 1, page_size: 8 }),
      evaApi.health(),
    ]);

    const oauthIsAvailable = tokenResult.status === 'fulfilled' || usersResult.status === 'fulfilled';
    const oauthError = tokenResult.status === 'rejected'
      ? tokenResult.reason?.message
      : usersResult.status === 'rejected'
        ? usersResult.reason?.message
        : '';

    setServicesStatus({
      oauth: oauthIsAvailable
        ? online('OAuth', usersResult.status === 'fulfilled' ? 'Авторизация и каталог пользователей доступны.' : 'Токен проверен.')
        : offline('OAuth', oauthError),
      eva: evaResult.status === 'fulfilled' && evaResult.value?.status === 'ok'
        ? online('EvaAI', 'Агент ответил на проверку работоспособности.')
        : offline('EvaAI', evaResult.status === 'rejected' ? evaResult.reason?.message : 'Агент вернул неизвестный статус.'),
    });

    if (usersResult.status === 'fulfilled') {
      const payload = usersResult.value;
      const apiStats = payload?.stats || {};
      const users = Array.isArray(payload?.results) ? [...payload.results] : [];
      users.sort((left, right) => new Date(right.created_date || 0) - new Date(left.created_date || 0));
      setStats({
        available: true,
        totalUsers: apiStats.total ?? payload?.count ?? null,
        activeUsers: apiStats.active ?? null,
        pendingUsers: apiStats.pending ?? null,
        newUsersLast7Days: apiStats.new_last_7_days ?? null,
      });
      setRecentUsers(users.slice(0, 5));
    } else {
      setStats(emptyStats);
      setRecentUsers([]);
    }

    setLastUpdated(new Date().toISOString());
    setIsLoading(false);
  }, [isAuthenticated]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const refreshAll = useCallback(async () => {
    await loadData();
  }, [loadData]);

  return (
    <AdminContext.Provider value={{
      stats,
      recentUsers,
      servicesStatus,
      telemetry: { available: false, detail: 'Телеметрия серверов ещё не подключена.' },
      isLoading,
      lastUpdated,
      loadData,
      refreshAll,
      refreshStats: refreshAll,
      refreshServices: refreshAll,
    }}>
      {children}
    </AdminContext.Provider>
  );
};
